import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    SaveIcon as Save,
    RefreshIcon as RefreshCw,
    EyeIcon as Eye,
    EyeOffIcon as EyeOff,
    SettingsIcon,
    KeyIcon,
    MailIcon,
    CloudQueueIcon as CloudIcon,
    BarChartIcon as AnalyticsIcon,
    SparklesIcon,
    LockIcon as FingerprintIcon,
    ExternalLinkIcon,
    CheckCircleIcon as CheckIcon,
    InfoIcon
} from '../components/Icons';

const DEFAULT_INTEGRATIONS = {
    ai: {
        enabled: true,
        apiKey: '',
        model: 'gemini-3.5-flash-lite',
        botName: 'Bob',
        publicChatEnabled: true,
        dashboardAgentEnabled: true
    },
    email: {
        enabled: true,
        provider: 'resend',
        apiKey: '',
        senderEmail: '',
        replyToEmail: ''
    },
    cloudflare: {
        enabled: true,
        apiToken: '',
        zoneId: '',
        accountId: '',
        domain: ''
    },
    googleAuth: {
        enabled: true,
        clientId: '',
        clientSecret: ''
    },
    analytics: {
        enabled: true,
        measurementId: ''
    },
    passkeys: {
        enabled: true,
        rpId: '',
        rpName: ''
    },
    externalLinks: {
        enabled: true,
        registrationUrl: '',
        bundUrl: '',
        bundLogoUrl: ''
    }
};

const AdminIntegrations = () => {
    const { user, API_URL } = useContext(AuthContext);
    const token = user?.token;
    const [settings, setSettings] = useState(DEFAULT_INTEGRATIONS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [showSecrets, setShowSecrets] = useState({});

    const authConfig = {
        headers: { Authorization: `Bearer ${token}` }
    };

    // Test state per key: { status: 'idle'|'testing'|'ok'|'error', message: '' }
    const [testResults, setTestResults] = useState({});

    const handleTestKey = async (service, apiKey, extra = {}) => {
        if (!apiKey?.trim()) return;
        setTestResults(prev => ({ ...prev, [service]: { status: 'testing', message: 'Teste...' } }));
        try {
            const { data } = await axios.post(`${API_URL}/setup/test-key`, { service, apiKey, ...extra }, authConfig);
            setTestResults(prev => ({
                ...prev,
                [service]: { status: data.ok ? 'ok' : 'error', message: data.message }
            }));
        } catch (err) {
            setTestResults(prev => ({
                ...prev,
                [service]: { status: 'error', message: err.response?.data?.message || 'Verbindungsfehler' }
            }));
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/settings/integrations`, authConfig);
                if (res.data) {
                    setSettings({
                        ...DEFAULT_INTEGRATIONS,
                        ...res.data,
                        ai: { ...DEFAULT_INTEGRATIONS.ai, ...(res.data.ai || {}) },
                        email: { ...DEFAULT_INTEGRATIONS.email, ...(res.data.email || {}) },
                        cloudflare: { ...DEFAULT_INTEGRATIONS.cloudflare, ...(res.data.cloudflare || {}) },
                        googleAuth: { ...DEFAULT_INTEGRATIONS.googleAuth, ...(res.data.googleAuth || {}) },
                        analytics: { ...DEFAULT_INTEGRATIONS.analytics, ...(res.data.analytics || {}) },
                        passkeys: { ...DEFAULT_INTEGRATIONS.passkeys, ...(res.data.passkeys || {}) },
                        externalLinks: { ...DEFAULT_INTEGRATIONS.externalLinks, ...(res.data.externalLinks || {}) }
                    });
                }
            } catch (err) {
                console.warn('Could not load integrations settings, using defaults:', err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [API_URL, token]);

    const handleToggleSecret = (key) => {
        setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);

        try {
            await axios.put(`${API_URL}/settings/integrations`, settings, authConfig);
            setMessage('Integrationen und Secrets erfolgreich gespeichert');
            setTimeout(() => setMessage(''), 4000);
        } catch (err) {
            setIsError(true);
            setMessage(err.response?.data?.message || 'Fehler beim Speichern der Einstellungen');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <RefreshCw className="animate-spin text-primary" sx={{ fontSize: 36 }} />
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header & Save Action */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-outlineVariant/30">
                <div>
                    <h2 className="text-headline-small font-bold text-surface-on">Schnittstellen & Secrets</h2>
                    <p className="text-body-medium text-surface-onVariant mt-1">
                        Aktiviere oder deaktiviere externe Dienste und verwalte die entsprechenden API-Keys und Zugangsdaten für eine saubere White-Label-Bereitstellung.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {message && (
                        <span className={`text-label-medium font-bold ${isError ? 'text-error' : 'text-primary'}`}>
                            {message}
                        </span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="md3-btn-filled flex items-center gap-2 px-6 h-12 shadow-sm"
                    >
                        {isSaving ? <RefreshCw className="animate-spin" sx={{ fontSize: 18 }} /> : <Save sx={{ fontSize: 18 }} />}
                        <span>Speichern</span>
                    </button>
                </div>
            </div>

            {/* Info Notice */}
            <div className="p-4 rounded-2xl bg-surface-containerHigh/60 border border-outlineVariant/40 flex items-start gap-3">
                <InfoIcon sx={{ fontSize: 20 }} className="text-primary mt-0.5 shrink-0" />
                <p className="text-body-small text-surface-onVariant leading-relaxed">
                    Wenn hier ein API-Key oder Secret leer gelassen wird, greift das System automatisch auf die standardmäßig konfigurierten Umgebungsvariablen des Servers zurück. Wird ein Modul deaktiviert, werden die zugehörigen UI-Elemente und Hintergrund-Aufrufe blockiert.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* 1. GOOGLE GEMINI KI-ASSISTENT */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.ai.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                <SparklesIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">KI-Assistent</h3>
                                <p className="text-body-small text-surface-onVariant">Google Gemini API & Prompts</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, ai: { ...prev.ai, enabled: !prev.ai.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.ai.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.ai.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.ai.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Gemini API Key</label>
                            <div className="relative">
                                <input
                                    type={showSecrets['aiApiKey'] ? 'text' : 'password'}
                                    value={settings.ai.apiKey || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, ai: { ...prev.ai, apiKey: e.target.value } }))}
                                    placeholder="AIzaSy..."
                                    className="md3-textfield-outlined w-full pr-10 font-mono text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleToggleSecret('aiApiKey')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-onVariant hover:text-surface-on"
                                >
                                    {showSecrets['aiApiKey'] ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
                                </button>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => handleTestKey('gemini', settings.ai.apiKey, { model: settings.ai.model })}
                                    disabled={!settings.ai.apiKey?.trim() || testResults['gemini']?.status === 'testing'}
                                    className="text-xs font-bold px-3 py-1 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-40 transition-all"
                                >
                                    {testResults['gemini']?.status === 'testing' ? '⏳ Teste...' : '⚡ Testen'}
                                </button>
                                {testResults['gemini'] && testResults['gemini'].status !== 'testing' && (
                                    <span className={`text-xs font-semibold ${testResults['gemini'].status === 'ok' ? 'text-green-600' : 'text-error'}`}>
                                        {testResults['gemini'].status === 'ok' ? '✅' : '❌'} {testResults['gemini'].message}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">KI-Modell</label>
                                <select
                                    value={settings.ai.model || 'gemini-3.5-flash-lite'}
                                    onChange={e => setSettings(prev => ({ ...prev, ai: { ...prev.ai, model: e.target.value } }))}
                                    className="md3-textfield-outlined w-full text-body-small"
                                >
                                    <option value="gemini-3.5-flash-lite">gemini-3.5-flash-lite (Empfohlen - Kostengünstig & Schnell)</option>
                                    <option value="gemini-3.1-flash-lite">gemini-3.1-flash-lite (Kostengünstig)</option>
                                    <option value="gemini-3.5-flash">gemini-3.5-flash (Leistungsstark)</option>
                                    <option value="gemini-3.6-flash">gemini-3.6-flash</option>
                                    <option value="gemini-3.7-flash">gemini-3.7-flash (Neueste Generation)</option>
                                    <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
                                    <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                                    <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
                                    <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">Bot-Name</label>
                                <input
                                    type="text"
                                    value={settings.ai.botName || 'Bob'}
                                    onChange={e => setSettings(prev => ({ ...prev, ai: { ...prev.ai, botName: e.target.value } }))}
                                    placeholder="Bob"
                                    className="md3-textfield-outlined w-full text-body-small"
                                />
                            </div>
                        </div>

                        <div className="pt-2 flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer text-body-small text-surface-on">
                                <input
                                    type="checkbox"
                                    checked={settings.ai.publicChatEnabled !== false}
                                    onChange={e => setSettings(prev => ({ ...prev, ai: { ...prev.ai, publicChatEnabled: e.target.checked } }))}
                                    className="rounded accent-primary w-4 h-4"
                                />
                                <span>Öffentlicher Website-Chat aktivieren</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-body-small text-surface-on">
                                <input
                                    type="checkbox"
                                    checked={settings.ai.dashboardAgentEnabled !== false}
                                    onChange={e => setSettings(prev => ({ ...prev, ai: { ...prev.ai, dashboardAgentEnabled: e.target.checked } }))}
                                    className="rounded accent-primary w-4 h-4"
                                />
                                <span>Dashboard KI-Agent aktivieren</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* 2. E-MAIL-VERSAND (RESEND) */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.email.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center">
                                <MailIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">E-Mail-Versand</h3>
                                <p className="text-body-small text-surface-onVariant">Transaktionale E-Mails (Resend API)</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, email: { ...prev.email, enabled: !prev.email.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.email.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.email.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.email.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Resend API Key</label>
                            <div className="relative">
                                <input
                                    type={showSecrets['emailApiKey'] ? 'text' : 'password'}
                                    value={settings.email.apiKey || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, email: { ...prev.email, apiKey: e.target.value } }))}
                                    placeholder="re_..."
                                    className="md3-textfield-outlined w-full pr-10 font-mono text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleToggleSecret('emailApiKey')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-onVariant hover:text-surface-on"
                                >
                                    {showSecrets['emailApiKey'] ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
                                </button>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => handleTestKey('resend', settings.email.apiKey)}
                                    disabled={!settings.email.apiKey?.trim() || testResults['resend']?.status === 'testing'}
                                    className="text-xs font-bold px-3 py-1 rounded-xl bg-secondary/10 text-secondary hover:bg-secondary/20 disabled:opacity-40 transition-all"
                                >
                                    {testResults['resend']?.status === 'testing' ? '⏳ Teste...' : '⚡ Testen'}
                                </button>
                                {testResults['resend'] && testResults['resend'].status !== 'testing' && (
                                    <span className={`text-xs font-semibold ${testResults['resend'].status === 'ok' ? 'text-green-600' : 'text-error'}`}>
                                        {testResults['resend'].status === 'ok' ? '✅' : '❌'} {testResults['resend'].message}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Absenderadresse (From)</label>
                            <input
                                type="text"
                                value={settings.email.senderEmail || ''}
                                onChange={e => setSettings(prev => ({ ...prev, email: { ...prev.email, senderEmail: e.target.value } }))}
                                placeholder="Stamm Name <noreply@deinedomain.de>"
                                className="md3-textfield-outlined w-full text-body-small"
                            />
                        </div>

                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Antwortadresse (Reply-To)</label>
                            <input
                                type="email"
                                value={settings.email.replyToEmail || ''}
                                onChange={e => setSettings(prev => ({ ...prev, email: { ...prev.email, replyToEmail: e.target.value } }))}
                                placeholder="kontakt@deinedomain.de"
                                className="md3-textfield-outlined w-full text-body-small"
                            />
                        </div>
                    </div>
                </div>

                {/* 3. CLOUDFLARE E-MAIL-ROUTING */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.cloudflare.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-tertiary/10 text-tertiary flex items-center justify-center">
                                <CloudIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">Cloudflare E-Mail-Routing</h3>
                                <p className="text-body-small text-surface-onVariant">Automatische Postfach-Weiterleitungen</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, cloudflare: { ...prev.cloudflare, enabled: !prev.cloudflare.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.cloudflare.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.cloudflare.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.cloudflare.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Cloudflare API Token</label>
                            <div className="relative">
                                <input
                                    type={showSecrets['cfApiToken'] ? 'text' : 'password'}
                                    value={settings.cloudflare.apiToken || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, cloudflare: { ...prev.cloudflare, apiToken: e.target.value } }))}
                                    placeholder="API-Token mit Email-Routing-Rechten"
                                    className="md3-textfield-outlined w-full pr-10 font-mono text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleToggleSecret('cfApiToken')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-onVariant hover:text-surface-on"
                                >
                                    {showSecrets['cfApiToken'] ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
                                </button>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => handleTestKey('cloudflare', settings.cloudflare.apiToken, { zoneId: settings.cloudflare.zoneId })}
                                    disabled={!settings.cloudflare.apiToken?.trim() || testResults['cloudflare']?.status === 'testing'}
                                    className="text-xs font-bold px-3 py-1 rounded-xl bg-tertiary/10 text-tertiary hover:bg-tertiary/20 disabled:opacity-40 transition-all"
                                >
                                    {testResults['cloudflare']?.status === 'testing' ? '⏳ Teste...' : '⚡ Testen'}
                                </button>
                                {testResults['cloudflare'] && testResults['cloudflare'].status !== 'testing' && (
                                    <span className={`text-xs font-semibold ${testResults['cloudflare'].status === 'ok' ? 'text-green-600' : 'text-error'}`}>
                                        {testResults['cloudflare'].status === 'ok' ? '✅' : '❌'} {testResults['cloudflare'].message}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">Zone ID</label>
                                <input
                                    type="text"
                                    value={settings.cloudflare.zoneId || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, cloudflare: { ...prev.cloudflare, zoneId: e.target.value } }))}
                                    placeholder="Zone ID aus Cloudflare"
                                    className="md3-textfield-outlined w-full font-mono text-xs"
                                />
                            </div>
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">E-Mail-Domain</label>
                                <input
                                    type="text"
                                    value={settings.cloudflare.domain || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, cloudflare: { ...prev.cloudflare, domain: e.target.value } }))}
                                    placeholder="pfadfinder-wutoeschingen.org"
                                    className="md3-textfield-outlined w-full text-body-small"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. GOOGLE OAUTH LOGIN */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.googleAuth.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                <KeyIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">Google Login (OAuth 2.0)</h3>
                                <p className="text-body-small text-surface-onVariant">Single Sign-On für Mitglieder</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, googleAuth: { ...prev.googleAuth, enabled: !prev.googleAuth.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.googleAuth.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.googleAuth.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.googleAuth.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Google Client ID</label>
                            <input
                                type="text"
                                value={settings.googleAuth.clientId || ''}
                                onChange={e => setSettings(prev => ({ ...prev, googleAuth: { ...prev.googleAuth, clientId: e.target.value } }))}
                                placeholder="...apps.googleusercontent.com"
                                className="md3-textfield-outlined w-full font-mono text-xs"
                            />
                        </div>

                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Google Client Secret</label>
                            <div className="relative">
                                <input
                                    type={showSecrets['googleClientSecret'] ? 'text' : 'password'}
                                    value={settings.googleAuth.clientSecret || ''}
                                    onChange={e => setSettings(prev => ({ ...prev, googleAuth: { ...prev.googleAuth, clientSecret: e.target.value } }))}
                                    placeholder="GOCSPX-..."
                                    className="md3-textfield-outlined w-full pr-10 font-mono text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleToggleSecret('googleClientSecret')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-onVariant hover:text-surface-on"
                                >
                                    {showSecrets['googleClientSecret'] ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. GOOGLE ANALYTICS */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.analytics.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center">
                                <AnalyticsIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">Google Analytics (GA4)</h3>
                                <p className="text-body-small text-surface-onVariant">Besucherstatistiken & Tracking</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, analytics: { ...prev.analytics, enabled: !prev.analytics.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.analytics.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.analytics.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.analytics.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">GA4 Measurement ID</label>
                            <input
                                type="text"
                                value={settings.analytics.measurementId || ''}
                                onChange={e => setSettings(prev => ({ ...prev, analytics: { ...prev.analytics, measurementId: e.target.value } }))}
                                placeholder="G-XXXXXXXXXX"
                                className="md3-textfield-outlined w-full font-mono text-xs uppercase"
                            />
                        </div>
                    </div>
                </div>

                {/* 6. PASSKEYS & BIOMETRIE */}
                <div className={`md3-card-elevated p-6 rounded-3xl border transition-all ${settings.passkeys.enabled ? 'bg-surface-containerLowest border-outlineVariant/50' : 'bg-surface-containerLowest/50 border-outlineVariant/20 opacity-80'}`}>
                    <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-tertiary/10 text-tertiary flex items-center justify-center">
                                <FingerprintIcon sx={{ fontSize: 22 }} />
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">Passkeys & WebAuthn</h3>
                                <p className="text-body-small text-surface-onVariant">Biometrischer Login per Fingerabdruck / Face ID</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, passkeys: { ...prev.passkeys, enabled: !prev.passkeys.enabled } }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${settings.passkeys.enabled
                                    ? 'bg-primary text-primary-on'
                                    : 'bg-surface-containerHighest text-surface-onVariant'
                                }`}
                        >
                            {settings.passkeys.enabled ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                    </div>

                    <div className={`space-y-4 pt-4 transition-all ${!settings.passkeys.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Relying Party ID (rpID)</label>
                            <input
                                type="text"
                                value={settings.passkeys.rpId || ''}
                                onChange={e => setSettings(prev => ({ ...prev, passkeys: { ...prev.passkeys, rpId: e.target.value } }))}
                                placeholder="Leer lassen für automatische Host-Erkennung"
                                className="md3-textfield-outlined w-full font-mono text-xs"
                            />
                            <span className="text-[10px] text-surface-onVariant opacity-70 mt-1 block">
                                Falls leer gelassen, wird die Domain dynamisch aus der aktuellen Dashboard-URL ermittelt.
                            </span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminIntegrations;
